import { ResourceType } from '@cotera/api';
import type { ResourcePickerCategory } from '@cotera/client/app/components/resource-picker/types';
import type { MentionColorVariant } from '@cotera/client/app/components/ui/tiptap/extensions';

export const resourceColors = {
  chat: {
    text: 'text-slate-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    dot: 'bg-slate-500',
    svg: '[&>svg]:text-slate-600',
  },
  agent: {
    text: 'text-mauve-600',
    bg: 'bg-mauve-50',
    border: 'border-mauve-200',
    dot: 'bg-mauve-500',
    svg: '[&>svg]:text-mauve-600',
  },
  workflow: {
    // Workflows keep their own color (earthy teal), distinct from triggers.
    text: 'text-pine-600',
    bg: 'bg-pine-50',
    border: 'border-pine-200',
    dot: 'bg-pine-500',
    svg: '[&>svg]:text-pine-600',
  },
  dataset: {
    text: 'text-spruce-600',
    bg: 'bg-spruce-50',
    border: 'border-spruce-200',
    dot: 'bg-spruce-500',
    svg: '[&>svg]:text-spruce-600',
  },
  document: {
    text: 'text-denim-600',
    bg: 'bg-denim-50',
    border: 'border-denim-200',
    dot: 'bg-denim-500',
    svg: '[&>svg]:text-denim-600',
  },
  trigger: {
    text: 'text-ochre-600',
    bg: 'bg-ochre-50',
    border: 'border-ochre-200',
    dot: 'bg-ochre-500',
    svg: '[&>svg]:text-ochre-600',
  },
  tool: {
    text: 'text-brown-600',
    bg: 'bg-brown-50',
    border: 'border-brown-200',
    dot: 'bg-brown-500',
    svg: '[&>svg]:text-brown-600',
  },
  folder: {
    text: 'text-sage-600',
    bg: 'bg-sage-50',
    border: 'border-sage-200',
    dot: 'bg-sage-500',
    svg: '[&>svg]:text-sage-600',
  },
} as const;

export type BadgeColorVariant =
  | 'primary'
  | 'blue'
  | 'green'
  | 'orange'
  | 'amber'
  | 'purple'
  | 'rose'
  | 'slate'
  | 'violet'
  | 'teal'
  | 'brown';

export type ResourceColorKey = keyof typeof resourceColors;

type ResourceTypeColorConfig = {
  resourceColor: ResourceColorKey;
  iconRingClassName: string;
  iconClassName: string;
};

export const resourceColorToBadgeVariant: Record<
  ResourceColorKey,
  BadgeColorVariant
> = {
  chat: 'slate',
  agent: 'violet',
  workflow: 'teal',
  dataset: 'blue',
  document: 'blue',
  trigger: 'amber',
  tool: 'brown',
  folder: 'green',
};

export const resourceColorToMentionVariant: Record<
  ResourceColorKey,
  MentionColorVariant
> = resourceColorToBadgeVariant;

export const resourceColorBadgeClassName = (
  color: ResourceColorKey
): string => {
  const classes = resourceColors[color];
  return `${classes.border} ${classes.bg} ${classes.text} ${classes.svg}`;
};

export const resourceColorDotClassName = (color: ResourceColorKey): string =>
  resourceColors[color].dot;

export const resourceColorBadgeVariant = (
  color: ResourceColorKey
): BadgeColorVariant => resourceColorToBadgeVariant[color];

export const resourceColorMentionVariant = (
  color: ResourceColorKey
): MentionColorVariant => resourceColorToMentionVariant[color];

export type HighlightedResourceType =
  | ResourceType.Agent
  | ResourceType.Document
  | ResourceType.Folder
  | ResourceType.WorkflowTrigger
  | ResourceType.Workflow
  | ResourceType.Dataset;

export const RESOURCE_TYPE_COLOR_CONFIG: Record<
  HighlightedResourceType,
  ResourceTypeColorConfig
> = {
  [ResourceType.Agent]: {
    resourceColor: 'agent',
    iconRingClassName: `${resourceColors.agent.bg} ${resourceColors.agent.text}`,
    iconClassName: `h-5 w-5 ${resourceColors.agent.text}`,
  },
  [ResourceType.Document]: {
    resourceColor: 'document',
    iconRingClassName: `${resourceColors.document.bg} ${resourceColors.document.text}`,
    iconClassName: `h-5 w-5 ${resourceColors.document.text}`,
  },
  [ResourceType.Folder]: {
    resourceColor: 'folder',
    iconRingClassName: `${resourceColors.folder.bg} ${resourceColors.folder.text}`,
    iconClassName: `h-5 w-5 ${resourceColors.folder.text}`,
  },
  [ResourceType.WorkflowTrigger]: {
    resourceColor: 'trigger',
    iconRingClassName: `${resourceColors.trigger.bg} ${resourceColors.trigger.text}`,
    iconClassName: `h-5 w-5 ${resourceColors.trigger.text}`,
  },
  [ResourceType.Workflow]: {
    resourceColor: 'workflow',
    iconRingClassName: `${resourceColors.workflow.bg} ${resourceColors.workflow.text}`,
    iconClassName: `h-5 w-5 ${resourceColors.workflow.text}`,
  },
  [ResourceType.Dataset]: {
    resourceColor: 'dataset',
    iconRingClassName: `${resourceColors.dataset.bg} ${resourceColors.dataset.text}`,
    iconClassName: `h-5 w-5 ${resourceColors.dataset.text}`,
  },
};

export const CHAT_RESOURCE_COLORS = {
  iconRingClassName: `${resourceColors.chat.bg} ${resourceColors.chat.text}`,
  iconClassName: `h-5 w-5 ${resourceColors.chat.text}`,
} as const;

const getHighlightedResourceType = (
  resourceType: unknown
): HighlightedResourceType | undefined => {
  if (
    resourceType === ResourceType.Agent ||
    resourceType === ResourceType.Document ||
    resourceType === ResourceType.Folder ||
    resourceType === ResourceType.WorkflowTrigger ||
    resourceType === ResourceType.Workflow ||
    resourceType === ResourceType.Dataset
  ) {
    return resourceType;
  }
  return undefined;
};

export const getResourceTypeFromMentionData = (
  mentionData: unknown
): HighlightedResourceType | undefined => {
  if (mentionData === null || mentionData === undefined) {
    return undefined;
  }
  if (typeof mentionData !== 'object') {
    return undefined;
  }
  if (Reflect.get(mentionData, 't') !== 'resource') {
    return undefined;
  }

  const payload = Reflect.get(mentionData, 'data');
  if (
    payload === null ||
    payload === undefined ||
    typeof payload !== 'object'
  ) {
    return undefined;
  }

  return getHighlightedResourceType(Reflect.get(payload, 'resourceType'));
};

/**
 * The `bg`+`text` classes for the color-coded bubble that sits behind a
 * resource's icon (or custom emoji) in list rows. Mirrors the "Add resource"
 * modal so the bubble color reliably signals the resource type — even when an
 * agent uses a custom emoji that has no inherent type color.
 */
export const resourceTypeIconRingClassName = (
  resourceType: ResourceType
): string => {
  const highlighted = getHighlightedResourceType(resourceType);
  if (highlighted !== undefined) {
    return RESOURCE_TYPE_COLOR_CONFIG[highlighted].iconRingClassName;
  }
  if (resourceType === ResourceType.Chat) {
    return CHAT_RESOURCE_COLORS.iconRingClassName;
  }
  // Neutral fallback for types without a dedicated palette entry.
  return CHAT_RESOURCE_COLORS.iconRingClassName;
};

export const getResourceMentionColorFromMentionData = (
  mentionData: unknown
): MentionColorVariant | undefined => {
  const resourceType = getResourceTypeFromMentionData(mentionData);
  if (resourceType === undefined) {
    return undefined;
  }
  return resourceColorMentionVariant(
    RESOURCE_TYPE_COLOR_CONFIG[resourceType].resourceColor
  );
};

export const getResourceBadgeColorFromMentionData = (
  mentionData: unknown
): BadgeColorVariant | undefined => {
  const resourceType = getResourceTypeFromMentionData(mentionData);
  if (resourceType === undefined) {
    return undefined;
  }
  return resourceColorBadgeVariant(
    RESOURCE_TYPE_COLOR_CONFIG[resourceType].resourceColor
  );
};

/**
 * Icon text-color classes for every resource-picker category.
 *
 * Resource types are derived from the resource color palette so there is exactly
 * one source of truth. `input` uses the folder/input green.
 */
export const CATEGORY_ICON_COLOR: Record<ResourcePickerCategory, string> = {
  agent: resourceColors.agent.text,
  document: resourceColors.document.text,
  folder: resourceColors.folder.text,
  trigger: resourceColors.trigger.text,
  dataset: resourceColors.dataset.text,
  tool: resourceColors.tool.text,
  workflow: resourceColors.workflow.text,
  input: resourceColors.folder.text,
  'flow-control': resourceColors.workflow.text,
};
